import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useFusionStore } from '../../stores/useFusionStore';
import { nodeById, edgeById } from '../../data/fusionSeed';
import { MODULE_COLOR, MODULE_LABEL, EDGE_LABEL, type FusionModule, type TxDetail } from '../../types/fusion';
import { caseFindingsService, type FindingItem } from '../../services/api';
import { buildWalletWorkbenchUrl, type FusionNodeData } from './workbenchLink';
import { FusionCaseSummary } from './FusionCaseSummary';
import { coverageGapNote, dormantNodeLabel, formatAmountsByToken, formatUtcDateTime, formatDwell, sumTransfersByToken as sumTransfers, type EdgeTransfer } from '../../services/caseCanvas';
import { expandableMembers } from './fusionMacro';
import { VISIBILITY } from '../../constants/visibility';
import { classify, formatCategoriesLine, type RiskClassification } from '../../utils/riskVocabulary';
import { WALLET_ROLE_LABEL, customerFacingName } from '../../constants/walletRole';
import { LedgerTransfersLine } from './LedgerTransfersLine';

const API_BASE_URL = (import.meta.env as unknown as Record<string, string>).VITE_API_URL || 'http://localhost:8000/api/v1';

/** CANVAS02: the data of the edges touching a canvas node (empty when the canvas is not at hand). */
function connectedEdgeData(id: string): Record<string, unknown>[] {
  const cy = (window as unknown as { __fusionCy?: CyLike }).__fusionCy;
  if (!cy || (cy.destroyed && cy.destroyed())) return [];
  const el = cy.getElementById(id);
  if (!el || !el.length || !el.connectedEdges) return [];
  return el.connectedEdges().map((x) => x.data());
}

const TIER_LABEL = ['cryptographic', 'legal / notice-return', 'deterministic registry', 'OSINT / scored'];

/** Live data for a canvas element (real-case nodes aren't in the demo seed). Guards a destroyed instance. */
type CyLike = {
  destroyed?: () => boolean;
  getElementById: (id: string) => {
    length: number; data: (name?: string, value?: unknown) => any;
    connectedEdges?: () => { map: <T>(f: (e: { data: () => Record<string, unknown> }) => T) => T[] };
  };
};
function liveData(id: string): Record<string, unknown> | null {
  const cy = (window as unknown as { __fusionCy?: CyLike }).__fusionCy;
  if (!cy || (cy.destroyed && cy.destroyed())) return null;
  const el = cy.getElementById(id);
  return el && el.length ? el.data() : null;
}

function TierBadge({ tier }: { tier: number }) {
  const c = tier <= 1
    ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/30'
    : tier === 2
      ? 'bg-primary/10 text-primary border border-primary/25'
      : 'bg-amber-500/15 text-amber-900 dark:text-amber-200 border border-amber-500/30';
  return <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${c}`}>Tier {tier} · {TIER_LABEL[tier]}</span>;
}

function DetBadge({ d }: { d: 'deterministic' | 'scored' }) {
  return d === 'deterministic'
    ? <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/30">deterministic</span>
    : <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/15 text-amber-900 dark:text-amber-200 border border-dashed border-amber-500/40">scored · inferred</span>;
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  const mono = k === 'id' || k === 'evidence' || k === 'chain';
  return (
    <div className="flex justify-between gap-3 py-0.5 text-xs">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className={`font-medium text-right break-all ${mono ? 'font-mono text-[11px]' : ''}`}>{v}</span>
    </div>
  );
}

/** Presentation keys that may linger in older saved data — never shown as evidence. */
const NON_EVIDENCE_EXTRAS = new Set(['classes']);

function Extras({ extras }: { extras?: Record<string, unknown> }) {
  const entries = Object.entries(extras || {}).filter(([k]) => !NON_EVIDENCE_EXTRAS.has(k));
  if (!entries.length) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Module data</div>
      {entries.map(([k, v]) => <Row key={k} k={k} v={String(Array.isArray(v) ? v.join(', ') : v)} />)}
    </div>
  );
}

/** Edge value with its own token/unit — never an assumed currency. */
function edgeValueText(value: number, unit?: string): string {
  const amount = value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return unit ? `${amount} ${unit}` : `${amount} (unit not recorded)`;
}

function hasDeepDive(dd: unknown): boolean {
  return Boolean(dd && typeof dd === 'object' && Object.keys(dd as Record<string, unknown>).length);
}

const amountsObj = (v: unknown): Record<string, number> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, number>) : undefined;

/** level=high cluster or group box: members, per-token totals, expand into the detail view. */
function ClusterExpand({ data, onExpand }: { data: Record<string, unknown>; onExpand?: (members: string[], clusterId: string) => void }) {
  const members = expandableMembers(data);
  if (!members.length) return null;
  const isBox = data.type === 'group_box';
  const inText = formatAmountsByToken(amountsObj(isBox ? data.amounts_in_by_token : data.amounts_by_token));
  const outText = formatAmountsByToken(amountsObj(isBox ? data.amounts_out_by_token : data.outflow_by_token));
  const count = isBox ? (data.wallet_count ?? members.length) : (data.member_count ?? members.length);
  return (
    <div className="mt-3 border-t border-border pt-2.5" data-testid="fusion-cluster-expand">
      <Row k={isBox ? 'wallets in group' : 'collapsed wallets'} v={String(count)} />
      {!!data.phase && <Row k="phase" v={String(data.phase)} />}
      {!!data.group && <Row k="group" v={String(data.group)} />}
      <Row k="value in" v={inText || 'not available'} />
      <Row k="value out" v={outText || 'not available'} />
      {typeof data.collapsed_edge_count === 'number' && <Row k="edges folded" v={String(data.collapsed_edge_count)} />}
      <p className="text-[10px] text-muted-foreground mt-1">
        {isBox
          ? 'A bundle the analysis declared (same phase and group). Totals are exact per-token sums of edges entering and leaving it; tokens are never added together.'
          : 'Relay wallets with no name, role or seizure status, folded together in the macro view. Nothing is dropped; totals are per token.'}
      </p>
      {onExpand && (
        <button
          type="button"
          onClick={() => onExpand(members, String(data.id))}
          className="mt-2 w-full rounded-md border border-border bg-secondary px-3 py-1.5 text-xs font-medium hover:bg-secondary/80 transition-colors"
        >
          {isBox ? 'Show group in detail view' : 'Expand in detail view'} ({members.length})
        </button>
      )}
    </div>
  );
}

/**
 * Per-token totals a producer declared on a node (e.g. a corridor feeder's
 * inflow over its window), each printed with the declared window, sample size
 * and truncated flag — a total without its basis is not shown bare.
 */
function DeclaredTotals({ data }: { data: Record<string, unknown> }) {
  if (Array.isArray(data.collapsed_members) || data.type === 'group_box') return null;
  const attr = (data.attribution && typeof data.attribution === 'object' ? data.attribution : {}) as Record<string, unknown>;
  const totals = Object.entries(attr).filter(([k, v]) => /_by_token$/.test(k) && v && typeof v === 'object' && !Array.isArray(v));
  if (!totals.length) return null;
  const win = attr.window_start || attr.window_end
    ? `${formatUtcDateTime(attr.window_start) || 'not available'} → ${formatUtcDateTime(attr.window_end) || 'not available'}`
    : 'not available';
  const sample = attr.sample_size !== undefined && attr.sample_size !== null ? String(attr.sample_size) : 'not available';
  const truncated = typeof attr.truncated === 'boolean' ? (attr.truncated ? 'yes' : 'no') : 'not available';
  return (
    <div className="mt-3 border-t border-border pt-2.5" data-testid="fusion-declared-totals">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Declared totals</div>
      {totals.map(([k, v]) => (
        <Row key={k} k={k.replace(/_by_token$/, '').replace(/_/g, ' ')} v={formatAmountsByToken(v as Record<string, number>) || 'not available'} />
      ))}
      <div className="text-[10px] text-muted-foreground mt-1">window {win} · sample {sample} · truncated {truncated}</div>
    </div>
  );
}

function FindingReviewBlock({ data, caseId }: { data: Record<string, unknown>; caseId?: string }) {
  const t = (data.seizure && typeof data.seizure === 'object' ? data.seizure : {}) as Record<string, unknown>;
  const address = typeof t.address === 'string' ? t.address : typeof data.address === 'string' ? data.address : '';
  const [finding, setFinding] = useState<FindingItem | null>(() => {
    return ((data.finding || t.finding) as FindingItem) || null;
  });

  useEffect(() => {
    if (data.finding || t.finding) {
      setFinding(((data.finding || t.finding) as FindingItem));
      return;
    }
    if (!caseId || !address) return;
    let cancelled = false;
    caseFindingsService.getFindings(caseId)
      .then((res) => {
        if (cancelled) return;
        const role = String(t.nexus_role || data.nexus_role || '').trim().toLowerCase();
        const chain = String(t.chain || data.chain || 'eth').trim().toLowerCase();
        const norm = chain === 'tron' || chain === 'trx' ? address.trim() : address.trim().toLowerCase();
        const key = `${role}:${chain}:${norm}`;
        const found = res.findings.find((f) => f.row_key === key || f.address.toLowerCase() === address.toLowerCase());
        if (found) setFinding(found);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [caseId, data, t, address]);

  const reviewStatus = finding?.status || (typeof t.review_status === 'string' ? t.review_status : typeof data.review_status === 'string' ? data.review_status : 'open');
  const owner = finding?.assignee_id ? String(finding.assignee_id) : (t.owner || t.assignee || data.owner || data.assignee || null);
  const comments = finding?.comments || (Array.isArray(t.comments) ? t.comments : Array.isArray(data.comments) ? data.comments : []);
  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;
  const lastCommentText = typeof lastComment === 'string' ? lastComment : (lastComment && typeof lastComment === 'object' ? (lastComment as { text?: string }).text : null);

  return (
    <div className="mt-3 border-t border-border pt-2" data-testid="fusion-finding-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Finding review</div>
        {caseId && (
          <Link
            to={`/cases/${caseId}/report#matrix`}
            data-tour="fusion-finding-review"
            data-testid="fusion-finding-review-link"
            className="text-[11px] text-primary hover:underline font-medium inline-flex items-center gap-1"
          >
            Review in Report &rarr;
          </Link>
        )}
      </div>
      <Row k="review status" v={<span className="font-semibold capitalize" data-testid="finding-review-status">{String(reviewStatus).replace('_', ' ')}</span>} />
      <Row k="owner" v={<span data-testid="finding-owner">{owner ? String(owner) : 'Unassigned'}</span>} />
      <Row
        k="last comment"
        v={lastCommentText ? (
          <span className="italic text-[11px]" data-testid="finding-last-comment">
            "{lastCommentText}"
          </span>
        ) : (
          <span className="text-muted-foreground text-[11px]" data-testid="finding-last-comment">None</span>
        )}
      />
    </div>
  );
}

/** Actions layer: one seizure-matrix target as a notice. Absent facts read "not available". */
function NoticePanel({ data, caseId }: { data: Record<string, unknown>; caseId?: string }) {
  const t = (data.seizure && typeof data.seizure === 'object' ? data.seizure : {}) as Record<string, unknown>;
  const demands = Array.isArray(t.statutory_demands) ? (t.statutory_demands as unknown[]).map(String) : [];
  const contact = t.contact;
  const amount = typeof t.amount === 'number'
    ? `${t.amount.toLocaleString('en-US', { maximumFractionDigits: 8 })}${t.token ? ` ${String(t.token)}` : ''}`
    : null;
  const address = typeof t.address === 'string' ? t.address : '';
  return (
    <div className="p-4" data-testid="fusion-notice-panel">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Action · notice</div>
      <div className="font-semibold text-sm mb-1.5 break-all">{customerFacingName(t) || String(t.entity_name || address || data.label || data.id)}</div>
      {data.evidence_missing === true && (
        <div className="mb-2 rounded-md border border-dashed border-red-500/60 bg-red-500/10 px-2 py-1.5 text-[11px] font-semibold text-red-700 dark:text-red-300">
          Evidence missing: no deposit transaction hash for this target. Do not serve until the deposit is evidenced.
        </div>
      )}
      <Row k="notice state" v={String(data.notice_state || 'not available')} />
      <Row k="template" v={String(t.notice_template_key || 'not available')} />
      <Row k="nexus role" v={String(t.nexus_role || 'not available')} />
      <Row k="wallet role" v={WALLET_ROLE_LABEL[String(t.wallet_role || data.wallet_role || '')] || 'not available'} />
      {!!address && <Row k="address" v={<span className="font-mono text-[11px]">{address}</span>} />}
      {!!t.chain && <Row k="chain" v={String(t.chain)} />}
      <Row k="tx hash" v={t.tx_hash ? <span className="font-mono text-[11px]">{String(t.tx_hash)}</span> : 'not available'} />
      <Row k="time" v={formatUtcDateTime(t.timestamp) || 'not available'} />
      <Row k="amount" v={amount || 'not available'} />
      {typeof t.value_usd === 'number' && <Row k="value (USD, from data)" v={`$${t.value_usd.toLocaleString()}`} />}
      <Row k="contact" v={contact ? (typeof contact === 'string' ? contact : JSON.stringify(contact)) : 'not available'} />
      <div className="mt-2 border-t border-border pt-2">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Demanded records</div>
        {demands.length
          ? <ul className="list-disc pl-4 space-y-0.5 text-[11px]">{demands.map((d, i) => <li key={i}>{d}</li>)}</ul>
          : <div className="text-[11px] text-muted-foreground">not available</div>}
      </div>
      <FindingReviewBlock data={data} caseId={caseId} />
      {!!address && <WorkbenchAction data={{ type: 'wallet', module: 'crypto', address, chain: t.chain }} caseId={caseId} />}
      <div className="mt-3 text-[10px] text-muted-foreground italic border-t border-border pt-2">
        From the seizure matrix of the latest case-worker run. The full notice prints in the case report.
      </div>
    </div>
  );
}

function Activity({ txns }: { txns?: TxDetail[] }) {
  if (!txns || !txns.length) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Activity · {txns.length} events</div>
      <div className="space-y-1.5">
        {txns.map((t) => (
          <div key={t.id} className="rounded-md border border-border bg-secondary/40 px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className={`text-[11px] font-semibold ${t.direction === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                {t.direction === 'in' ? '↓ in' : '↑ out'}{t.value ? ` · ${t.value.toLocaleString()} ${t.unit}` : ''}
              </span>
              <span className="text-[9px] text-muted-foreground">{new Date(t.ts).toLocaleString()}</span>
            </div>
            <div className="text-[11px] truncate">{t.counterparty}</div>
            <div className="text-[9px] text-muted-foreground font-mono break-all">{t.kind ? `${t.kind} · ` : ''}{t.ref}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkbenchAction({ data, caseId }: { data: FusionNodeData; caseId?: string }) {
  const navigate = useNavigate();
  const url = buildWalletWorkbenchUrl(data, caseId);
  if (!url) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(url)}
      className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-brand-gold-hover transition-colors"
      aria-label="Open wallet in Workbench for crypto drill-down"
      title="Opens this wallet in crypto Workbench with the bound case"
    >
      Open in Workbench <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

function SemanticRoleChip({ role }: { role: string }) {
  const meta: Record<string, { label: string; icon: string; style: string }> = {
    suspect_hub: { label: 'Suspect hub', icon: '★', style: 'bg-amber-500/20 text-amber-900 dark:text-amber-300 border-amber-500/40' },
    illicit_sink: { label: 'Flagged sink', icon: '!', style: 'bg-rose-500/20 text-rose-900 dark:text-rose-300 border-rose-500/40' },
    mule_conduit: { label: 'Pass-through conduit (candidate)', icon: '→', style: 'bg-amber-600/20 text-amber-900 dark:text-amber-200 border-amber-600/40' },
    capital_feeder: { label: 'Capital feeder', icon: '↑', style: 'bg-blue-500/20 text-blue-900 dark:text-blue-300 border-blue-500/40' },
    cex_source: { label: 'Exchange source', icon: '↑', style: 'bg-blue-500/20 text-blue-900 dark:text-blue-300 border-blue-500/40' },
    p2p_bridge: { label: 'P2P bridge', icon: '⇄', style: 'bg-purple-500/20 text-purple-900 dark:text-purple-300 border-purple-500/40' },
    corporate_anchor: { label: 'Corporate entity', icon: '▣', style: 'bg-indigo-500/20 text-indigo-900 dark:text-indigo-300 border-indigo-500/40' },
    victim_depositor: { label: 'Victim depositor', icon: '•', style: 'bg-slate-500/20 text-slate-800 dark:text-slate-200 border-slate-500/40' },
  };
  const r = meta[role] || { label: role.replace(/_/g, ' '), icon: '•', style: 'bg-secondary text-secondary-foreground border-border' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.style}`}>
      <span>{r.icon}</span> {r.label}
    </span>
  );
}

function CryptoDeepDivePanel({ data }: { data: FusionNodeData }) {
  const dd = (data.deep_dive || {}) as Record<string, unknown>;
  const freeze = (data.freeze_status || dd.freeze_status) as { is_frozen?: boolean; issuer?: string; checked?: boolean } | undefined;
  const isSink = Boolean(data.semantic_role === 'illicit_sink' || data.type === 'sink' || dd.is_black_usdt_sink);

  return (
    <div className="mt-3 border-t border-border pt-2.5">
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="text-[10px] uppercase font-bold tracking-wider text-primary">Crypto Deep Dive</div>
        {freeze?.checked && (
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${freeze.is_frozen ? 'bg-rose-500 text-white' : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'}`}>
            {freeze.is_frozen ? 'FROZEN (Tether)' : 'UNFROZEN (Actionable)'}
          </span>
        )}
        {isSink && !freeze?.checked && (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-500/20 text-rose-700 dark:text-rose-300">
            Black USDT Sink
          </span>
        )}
      </div>

      {typeof dd.volume_by_token === 'object' && dd.volume_by_token !== null && (
        <Row
          k="volume (by token)"
          v={Object.entries(dd.volume_by_token as Record<string, number>)
            .map(([tok, amt]) => `${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${tok}`)
            .join(' · ')}
        />
      )}
      {typeof dd.total_volume_usd === 'number' && (
        <Row k="priced volume (USD)" v={`$${Number(dd.total_volume_usd).toLocaleString()}`} />
      )}
      {dd.ocr_healed === true && (
        <Row k="provenance" v="⚠ resolved from OCR/truncated seed (inferred)" />
      )}
      {typeof dd.feeder_cex === 'string' && (
        <Row k="capital source" v={String(dd.feeder_cex)} />
      )}
    </div>
  );
}

function isViewerUser(): boolean {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    return u?.role === 'viewer';
  } catch {
    return false;
  }
}

interface AnnotationItem {
  id: string;
  kind: 'note' | 'label';
  source: string;
  assertion_key: string;
  text: string;
  officer: string;
  time?: string | null;
  created_at?: string | null;
}

export function NodeNotesBlock({ data, caseId }: { data: Record<string, unknown>; caseId?: string }) {
  const nodeId = String(data.id || '');
  const [annotations, setAnnotations] = useState<AnnotationItem[]>([]);
  const [currentLabel, setCurrentLabel] = useState<string | null>(
    typeof data.officer_label === 'string' && data.officer_label.trim() ? data.officer_label.trim() : null
  );
  const [noteCount, setNoteCount] = useState<number>(Number(data.note_count) || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'note' | 'label'>('note');
  const [noteText, setNoteText] = useState('');
  const [labelText, setLabelText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isViewer = isViewerUser();

  useEffect(() => {
    if (!caseId || !nodeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token') || '';
    fetch(`${API_BASE_URL}/canvas/${caseId}/nodes/${encodeURIComponent(nodeId)}/annotations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 404) return null;
          const err = await res.json().catch(() => ({}));
          throw new Error(String(err.detail || `Failed to fetch annotations (${res.status})`));
        }
        return res.json();
      })
      .then((resData) => {
        if (cancelled || !resData) return;
        setAnnotations(Array.isArray(resData.annotations) ? resData.annotations : []);
        if (resData.current_label !== undefined) setCurrentLabel(resData.current_label);
        if (resData.note_count !== undefined) setNoteCount(resData.note_count);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, nodeId]);

  const handleAddAnnotation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!caseId || !nodeId || isViewer) return;
    setSubmitting(true);
    setError(null);
    try {
      const text = activeTab === 'note' ? noteText.trim() : labelText.trim();
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/nodes/${encodeURIComponent(nodeId)}/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ kind: activeTab, text }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(String(errJson.detail || `Save failed with status ${res.status}`));
      }
      const created = await res.json();
      setAnnotations((prev) => [...prev, created]);
      if (created.current_label !== undefined) setCurrentLabel(created.current_label);
      if (created.note_count !== undefined) setNoteCount(created.note_count);

      // Immediately update Cytoscape canvas element
      const cy = (window as unknown as { __fusionCy?: CyLike }).__fusionCy;
      if (cy) {
        const el = cy.getElementById(nodeId);
        if (el && el.length) {
          if (activeTab === 'label') {
            el.data('officer_label', text || undefined);
            el.data('label', text || el.data('system_label') || el.data('id'));
          } else if (activeTab === 'note') {
            const newCount = (el.data('note_count') || 0) + 1;
            el.data('note_count', newCount);
            el.data('has_notes', true);
          }
        }
      }

      if (activeTab === 'note') setNoteText('');
      else setLabelText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearLabel = async () => {
    if (!caseId || !nodeId || isViewer) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/nodes/${encodeURIComponent(nodeId)}/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ kind: 'label', text: '' }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(String(errJson.detail || `Clear failed with status ${res.status}`));
      }
      const created = await res.json();
      setAnnotations((prev) => [...prev, created]);
      setCurrentLabel(null);
      const cy = (window as unknown as { __fusionCy?: CyLike }).__fusionCy;
      if (cy) {
        const el = cy.getElementById(nodeId);
        if (el && el.length) {
          el.data('officer_label', undefined);
          el.data('label', el.data('system_label') || el.data('id'));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-tour="fusion-node-notes"
      data-testid="fusion-node-notes"
      className="mt-3 rounded-md border border-border bg-card p-2.5 text-xs space-y-2"
    >
      <div className="flex items-center justify-between font-semibold text-foreground">
        <span>Officer Notes & Labels</span>
        {noteCount > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-primary/10 text-primary font-bold">
            {noteCount} {noteCount === 1 ? 'note' : 'notes'}
          </span>
        )}
      </div>

      {currentLabel && (
        <div className="text-[11px] bg-secondary/50 rounded px-2 py-1 flex items-center justify-between">
          <span>Private label: <strong className="font-semibold text-foreground">{currentLabel}</strong></span>
          {!isViewer && caseId && (
            <button
              type="button"
              data-testid="fusion-clear-label-btn"
              onClick={handleClearLabel}
              disabled={submitting}
              className="text-[10px] text-destructive hover:underline ml-2"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* History */}
      <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="fusion-notes-history">
        {loading && <div className="text-muted-foreground italic text-[11px]">Loading notes…</div>}
        {!loading && annotations.length === 0 && (
          <div className="text-muted-foreground italic text-[11px]">No officer notes or labels recorded.</div>
        )}
        {annotations.map((a, i) => (
          <div key={a.id || i} className="rounded border border-border bg-secondary/30 p-1.5 text-[10px]" data-testid="fusion-note-item">
            <div className="flex justify-between items-center text-muted-foreground mb-0.5">
              <span className={`px-1 rounded text-[9px] font-bold ${a.kind === 'label' ? 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' : 'bg-amber-500/15 text-amber-800 dark:text-amber-200'}`}>
                {a.kind.toUpperCase()}
              </span>
              <span>{a.officer ? `${a.officer} · ` : ''}{formatUtcDateTime(a.time || a.created_at) || ''}</span>
            </div>
            <div className="text-foreground break-words">{a.text || (a.kind === 'label' ? '(cleared)' : '')}</div>
          </div>
        ))}
      </div>

      {error && <div className="text-destructive text-[11px]">{error}</div>}

      {/* Action Form */}
      {isViewer ? (
        <div className="text-muted-foreground italic text-[10px] border-t border-border pt-1.5" data-testid="fusion-notes-viewer-readonly">
          Read-only for viewers.
        </div>
      ) : caseId ? (
        <form onSubmit={handleAddAnnotation} className="border-t border-border pt-2 space-y-1.5" data-testid="fusion-annotation-form">
          <div className="flex gap-2 text-[10px] font-semibold text-muted-foreground mb-1">
            <button
              type="button"
              onClick={() => setActiveTab('note')}
              className={`pb-0.5 border-b-2 ${activeTab === 'note' ? 'border-primary text-primary' : 'border-transparent hover:text-foreground'}`}
            >
              Add Note
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('label')}
              className={`pb-0.5 border-b-2 ${activeTab === 'label' ? 'border-primary text-primary' : 'border-transparent hover:text-foreground'}`}
            >
              Set Private Label
            </button>
          </div>

          {activeTab === 'note' ? (
            <div>
              <textarea
                data-testid="fusion-note-input"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter officer note (up to 2,000 characters)"
                maxLength={2000}
                rows={2}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
                required
              />
            </div>
          ) : (
            <div>
              <input
                data-testid="fusion-label-input"
                type="text"
                value={labelText}
                onChange={(e) => setLabelText(e.target.value)}
                placeholder="Private label (up to 60 characters)"
                maxLength={60}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="fusion-add-annotation-btn"
            className="w-full rounded bg-primary text-primary-foreground py-1 text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Saving…' : activeTab === 'note' ? 'Add Note' : 'Set Label'}
          </button>
        </form>
      ) : null}
    </div>
  );
}

export function nodeRiskClassification(data: Record<string, unknown>): RiskClassification | null {
  const attr = (data.attribution && typeof data.attribution === 'object'
    ? data.attribution
    : {}) as Record<string, unknown>;
  const dd = (data.deep_dive && typeof data.deep_dive === 'object'
    ? data.deep_dive
    : {}) as Record<string, unknown>;

  const rows: Record<string, unknown>[] = [];

  // 1. Sanctions hits (Level: Listed, Category: sanctions_list)
  const sanctionsHits = data.sanctions_hits || attr.sanctions_hits;
  if (Array.isArray(sanctionsHits) && sanctionsHits.length > 0) {
    for (const h of sanctionsHits) {
      if (h && typeof h === 'object') {
        rows.push({
          rule: 'sanctions_list',
          source: 'sanctions',
          relevance: 'major_crime',
          strength: 'listed',
          ...(h as Record<string, unknown>),
        });
      } else {
        rows.push({
          rule: 'sanctions_list',
          source: 'sanctions',
          relevance: 'major_crime',
          strength: 'listed',
          label: String(h),
        });
      }
    }
  }

  // 2. Issuer blacklist / freeze status (Level: Listed, Category: issuer_blacklist)
  const freezeStatus = (data.freeze_status || attr.freeze_status || dd.freeze_status) as
    | { is_frozen?: boolean; is_blacklisted?: boolean; issuer?: string; checked?: boolean }
    | undefined;
  const isBlacklisted = Boolean(
    data.blacklisted === true ||
    attr.blacklisted === true ||
    data.is_blacklisted === true ||
    attr.is_blacklisted === true ||
    data.is_frozen === true ||
    attr.is_frozen === true ||
    freezeStatus?.is_frozen === true ||
    freezeStatus?.is_blacklisted === true
  );

  if (isBlacklisted) {
    const issuer = freezeStatus?.issuer || (data.live_source as string) || (attr.live_source as string) || 'issuer_freeze';
    rows.push({
      rule: 'issuer_blacklist',
      category: 'issuer_blacklist',
      blacklisted: true,
      live_source: issuer,
      live_rule: 'blacklisted',
      strength: 'listed',
    });
  }

  // 3. Risk signals (attr.risk_signals or data.risk_signals)
  const riskSignals = data.risk_signals || attr.risk_signals;
  if (Array.isArray(riskSignals) && riskSignals.length > 0) {
    for (const sig of riskSignals) {
      if (sig && typeof sig === 'object') {
        rows.push(sig as Record<string, unknown>);
      } else if (typeof sig === 'string') {
        const sigLower = sig.toLowerCase();
        if (sigLower.includes('sanction')) {
          rows.push({ rule: 'sanctions_list', source: 'sanctions', relevance: 'major_crime', strength: 'listed' });
        } else if (sigLower.includes('blacklist') || sigLower.includes('freeze')) {
          rows.push({ rule: 'issuer_blacklist', blacklisted: true, live_source: 'issuer_freeze', strength: 'listed' });
        } else if (sigLower.includes('mixer')) {
          rows.push({ rule: 'mixer_at_scale', relevance: 'major_crime', strength: 'indicator', source: 'entity_db' });
        } else if (sigLower.includes('cyber') || sigLower.includes('scam') || sigLower.includes('fraud')) {
          rows.push({ rule: 'cyber_category', relevance: 'cybercrime', strength: 'listed', source: 'entity_db' });
        }
      }
    }
  }

  // 4. Exposure (e.g. attr.exposure or data.exposure)
  const exposure = data.exposure || attr.exposure;
  if (exposure) {
    if (typeof exposure === 'string') {
      if (exposure.toLowerCase().includes('mixer')) {
        rows.push({ rule: 'mixer_at_scale', relevance: 'major_crime', strength: 'indicator', source: 'entity_db' });
      }
    } else if (typeof exposure === 'object') {
      const expObj = exposure as Record<string, unknown>;
      if (expObj.mixer || expObj.mixer_exposure) {
        rows.push({ rule: 'mixer_at_scale', relevance: 'major_crime', strength: 'indicator', source: 'entity_db' });
      }
    }
  }

  // 5. Cyber-attributed (attr.cyber_attributed, data.cyber_attributed)
  const cyberAttributed = data.cyber_attributed || attr.cyber_attributed || data.cyber_category || attr.cyber_category;
  if (cyberAttributed) {
    rows.push({
      rule: 'cyber_category',
      relevance: 'cybercrime',
      strength: 'listed',
      source: 'entity_db',
    });
  }

  if (rows.length === 0) {
    return null;
  }

  return classify(rows);
}

function NodeRiskLevel({ data }: { data: Record<string, unknown> }) {
  const cls = nodeRiskClassification(data);
  if (!cls) return null;
  const catText = formatCategoriesLine(cls.categories);
  const suppCount = cls.suppressed_count || 0;
  return (
    <div className="mt-2 border-t border-border pt-2" data-testid="fusion-risk-level">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Risk evaluation</div>
      <Row
        k="level"
        v={
          <>
            <span>{cls.level}</span>
            {suppCount > 0 && (
              <span className="text-muted-foreground font-normal text-[11px] ml-1" data-testid="fusion-risk-suppressed">
                ({suppCount} suppressed)
              </span>
            )}
          </>
        }
      />
      {suppCount > 0 && (
        <Row
          k="suppressed"
          v={
            <span data-testid="fusion-inspector-suppressed-mark" className="text-amber-700 dark:text-amber-400 font-semibold text-[11px]">
              [suppressed: {suppCount} hit{suppCount === 1 ? '' : 's'}]
            </span>
          }
        />
      )}
      {catText ? (
        <Row k="categories" v={catText} />
      ) : cls.untested_sources.length > 0 ? (
        <Row k="untested" v={`${cls.untested_sources.join(', ')} not tested`} />
      ) : null}
    </div>
  );
}

export function LinkToSubjectAction({ data, caseId }: { data: Record<string, unknown>; caseId?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState<'onchain' | 'document' | 'judgement'>('onchain');
  const [citations, setCitations] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const walletAddress = String(data.address || data.canonical_id || data.id || '');

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (grade === 'onchain' && !citations.trim()) {
      setError('At least one transaction hash citation is required for onchain grade');
      return;
    }
    if (grade === 'document' && !citations.trim()) {
      setError('At least one evidence ID citation is required for document grade');
      return;
    }
    if (grade === 'judgement' && reason.trim().length < 20) {
      setError('Reason must be at least 20 characters for judgement grade');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || '';
      const citList = citations.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      const body = {
        subject: subject.trim(),
        wallets: [walletAddress],
        grade,
        citations: citList,
        reason: reason.trim(),
      };
      const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/subject-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(String(errJson.detail || `Request failed with status ${res.status}`));
      }
      setSuccess('Subject link created');
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(null);
        setSubject('');
        setCitations('');
        setReason('');
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        data-tour="fusion-subject-link"
        data-testid="fusion-link-subject-btn"
        onClick={() => setIsOpen(true)}
        className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border border-[#0891b2]/40 bg-[#0891b2]/10 px-3 py-1.5 text-xs font-semibold text-[#0891b2] hover:bg-[#0891b2]/20 transition-colors"
      >
        Link to a subject
      </button>
    );
  }

  return (
    <form
      data-tour="fusion-subject-link"
      data-testid="fusion-subject-link-form"
      onSubmit={handleSubmit}
      className="mt-3 rounded-md border border-border bg-card p-2.5 text-xs space-y-2"
    >
      <div className="flex items-center justify-between font-semibold text-foreground">
        <span>Link to a subject</span>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-muted-foreground hover:text-foreground text-xs"
        >
          Cancel
        </button>
      </div>

      <div>
        <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-0.5">Subject</label>
        <input
          data-testid="fusion-subject-input"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject name or canonical id"
          className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
          required
        />
      </div>

      <div>
        <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-0.5">Evidence Grade</label>
        <select
          data-testid="fusion-grade-select"
          value={grade}
          onChange={(e) => setGrade(e.target.value as 'onchain' | 'document' | 'judgement')}
          className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
        >
          <option value="onchain">onchain (requires tx hash)</option>
          <option value="document">document (requires evidence ID)</option>
          <option value="judgement">judgement (reason ≥ 20 chars)</option>
        </select>
      </div>

      {grade !== 'judgement' && (
        <div>
          <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-0.5">
            {grade === 'onchain' ? 'Transaction Hashes' : 'Evidence IDs'}
          </label>
          <input
            data-testid="fusion-citations-input"
            type="text"
            value={citations}
            onChange={(e) => setCitations(e.target.value)}
            placeholder={grade === 'onchain' ? '0x... or TRX tx hash' : 'Case evidence UUID'}
            className="w-full rounded border border-input bg-background px-2 py-1 text-xs font-mono"
            required
          />
        </div>
      )}

      <div>
        <label className="block text-[10px] uppercase font-bold text-muted-foreground mb-0.5">Reason</label>
        <textarea
          data-testid="fusion-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={grade === 'judgement' ? 'Mandatory reason (min 20 characters)' : 'Officer justification'}
          rows={2}
          className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
          required={grade === 'judgement'}
        />
      </div>

      {error && <div className="text-[11px] text-destructive">{error}</div>}
      {success && <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">{success}</div>}

      <button
        type="submit"
        data-testid="fusion-submit-link-btn"
        disabled={submitting}
        className="w-full rounded bg-[#0891b2] px-3 py-1.5 font-semibold text-white hover:bg-[#0891b2]/90 disabled:opacity-50"
      >
        {submitting ? 'Linking…' : 'Create Subject Link'}
      </button>
    </form>
  );
}

function SubjectLinkInspectorPanel({ data, edgeId, caseId }: { data: Record<string, unknown>; edgeId: string; caseId?: string }) {
  const [retracting, setRetracting] = useState(false);
  const [retractReason, setRetractReason] = useState('');
  const [showRetractForm, setShowRetractForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRetracted = data.retracted === true;
  const grade = String(data.grade || 'judgement');
  const citations = Array.isArray(data.citations) ? (data.citations as unknown[]).map(String) : [];
  const assertions = Array.isArray(data.assertions) ? (data.assertions as Record<string, unknown>[]) : [];
  const srcLabel = String(liveData(String(data.source))?.label || data.source || '');
  const tgtLabel = String(liveData(String(data.target))?.label || data.target || '');

  const handleRetract = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!retractReason.trim()) {
      setError('Retraction reason is required');
      return;
    }
    setRetracting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/subject-links/${edgeId}/retract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: retractReason.trim() }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(String(errJson.detail || `Retract failed with status ${res.status}`));
      }
      setShowRetractForm(false);
      const cy = (window as unknown as { __fusionCy?: CyLike }).__fusionCy;
      if (cy) {
        const el = cy.getElementById(edgeId);
        if (el && el.length) {
          el.data('retracted', true);
          el.data('_visibility', VISIBILITY.EARLIER_RUN);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetracting(false);
    }
  };

  return (
    <div className="p-4" data-testid="fusion-subject-link-inspector">
      <div className="flex items-center justify-between gap-1 mb-1">
        <span className="text-[10px] uppercase font-bold tracking-wide text-[#0891b2]">Subject Link</span>
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isRetracted ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300' : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'}`}>
          {isRetracted ? 'RETRACTED' : 'ACTIVE'}
        </span>
      </div>

      <div className="text-sm font-medium mb-2 break-all">{srcLabel} → {tgtLabel}</div>
      <div className="mb-2">
        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-[#0891b2]/15 text-[#0891b2] border border-[#0891b2]/30">
          Grade: {grade}
        </span>
      </div>

      <Row k="type" v="subject_link" />
      <Row k="grade" v={grade} />
      {data.officer_id ? <Row k="officer" v={String(data.officer_id)} /> : null}
      {data.created_at ? <Row k="linked at" v={formatUtcDateTime(data.created_at) || String(data.created_at)} /> : null}
      {data.reason ? <Row k="reason" v={String(data.reason)} /> : null}

      {citations.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Citations · {citations.length}</div>
          <div className="space-y-1">
            {citations.map((c) => (
              <div key={c} className="font-mono text-[10px] break-all bg-secondary/40 px-1.5 py-0.5 rounded">{c}</div>
            ))}
          </div>
        </div>
      )}

      {isRetracted && (
        <div className="mt-2 border-t border-border pt-2 text-amber-800 dark:text-amber-200">
          <div className="text-[10px] uppercase tracking-wide font-bold mb-1">Retraction Details</div>
          {data.retracted_at ? <Row k="retracted at" v={formatUtcDateTime(data.retracted_at) || String(data.retracted_at)} /> : null}
          {data.retracted_by ? <Row k="retracted by" v={String(data.retracted_by)} /> : null}
          {data.retraction_reason ? <Row k="retraction reason" v={String(data.retraction_reason)} /> : null}
        </div>
      )}

      {assertions.length > 0 && (
        <div className="mt-3 border-t border-border pt-2" data-testid="fusion-link-assertions">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Assertion History ({assertions.length})</div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {assertions.map((a, i) => {
              const p = (a.payload || {}) as Record<string, unknown>;
              return (
                <div key={i} className="rounded border border-border bg-secondary/30 p-1.5 text-[10px]">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{String(a.source || 'assertion')}</span>
                    <span>{formatUtcDateTime(a.created_at) || String(a.created_at || '')}</span>
                  </div>
                  {p.retracted === true ? (
                    <div className="text-amber-600 font-semibold mt-0.5">Retracted: {String(p.reason || '')}</div>
                  ) : (
                    <div>Grade {String(p.grade || grade)}: {String(p.reason || '')}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isRetracted && caseId && (
        <div className="mt-3 border-t border-border pt-2">
          {!showRetractForm ? (
            <button
              type="button"
              data-testid="fusion-retract-link-btn"
              onClick={() => setShowRetractForm(true)}
              className="w-full rounded border border-destructive/40 text-destructive bg-destructive/10 px-2.5 py-1 text-xs font-semibold hover:bg-destructive/20 transition-colors"
            >
              Retract link
            </button>
          ) : (
            <form onSubmit={handleRetract} className="space-y-1.5">
              <label className="block text-[10px] uppercase font-bold text-destructive">Retraction reason</label>
              <textarea
                value={retractReason}
                onChange={(e) => setRetractReason(e.target.value)}
                placeholder="Reason for withdrawing this link"
                rows={2}
                className="w-full rounded border border-input bg-background p-1.5 text-xs"
                required
              />
              {error && <div className="text-[10px] text-destructive">{error}</div>}
              <div className="flex gap-1.5">
                <button
                  type="submit"
                  disabled={retracting}
                  className="flex-1 rounded bg-destructive text-white py-1 text-xs font-semibold hover:bg-destructive/90"
                >
                  {retracting ? 'Retracting…' : 'Confirm Retract'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRetractForm(false)}
                  className="rounded border border-input px-2 py-1 text-xs text-muted-foreground"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export function FusionInspector({ caseId, onExpandCluster }: {
  caseId?: string;
  /** Macro view: expand a collapsed cluster (member ids) into the detail view. */
  onExpandCluster?: (memberIds: string[], clusterId: string) => void;
} = {}) {
  const selected = useFusionStore((s) => s.selected);

  if (!selected) {
    return (
      <div className="p-4 space-y-3" data-testid="fusion-inspector-empty">
        <div>
          <div className="font-semibold text-sm text-foreground mb-0.5">Case overview</div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Brief summary of the fused graph. Click a node or edge for provenance and activity.
          </p>
        </div>
        <FusionCaseSummary layout="stack" source={caseId ? 'canvas' : 'seed'} />
      </div>
    );
  }

  if (selected.kind === 'node') {
    const n = nodeById(selected.id);
    if (!n) {
      // Real-case node — projected from the case graph, not a rich demo-seed node. Render honestly.
      const d = liveData(selected.id);
      if (!d) return null;
      if (d.type === 'notice') return <NoticePanel data={d} caseId={caseId} />;
      const mod = (MODULE_COLOR[d.module as FusionModule] ? d.module : 'crypto') as FusionModule;
      const risk = typeof d.risk_score === 'number' ? d.risk_score : undefined;
      const semanticRole = typeof d.semantic_role === 'string' ? d.semantic_role : undefined;
      return (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: MODULE_COLOR[mod] }} />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{MODULE_LABEL[mod]} · {String(d.type || 'node')}</span>
          </div>
          <div className="font-semibold text-sm mb-0.5 break-all" data-testid="fusion-node-title">
            {customerFacingName(d) || String(d.label || d.officer_label || d.id || selected.id)}
          </div>
          {WALLET_ROLE_LABEL[String(d.wallet_role || '')] && (
            <div className="text-xs text-muted-foreground mb-1.5" data-testid="fusion-wallet-role">
              {WALLET_ROLE_LABEL[String(d.wallet_role)]}
            </div>
          )}
          {Boolean(d.officer_label && d.system_label && d.officer_label !== d.system_label) && (
            <div className="text-xs text-muted-foreground mb-1.5 break-all" data-testid="fusion-system-label">
              System label: <span className="font-mono text-[11px]">{String(d.system_label)}</span>
            </div>
          )}
          {(semanticRole || d.seizure_target === true || !!d.related_case) && (
            <div className="mb-2 flex flex-wrap gap-1">
              {!!d.related_case && (
                <span
                  data-testid="related-case-chip"
                  title={String(d.related_case_label || 'Related case')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-violet-500/15 text-violet-900 dark:text-violet-200 border-violet-500/40"
                >
                  {String(d.related_case_label || 'Related case')}
                </span>
              )}
              {semanticRole && <SemanticRoleChip role={semanticRole} />}
              {d.seizure_target === true && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-blue-500/15 text-blue-900 dark:text-blue-200 border-blue-500/40">
                  Seizure target
                </span>
              )}
            </div>
          )}
          {d.dormant === true && (
            <div
              data-testid="fusion-dormant-note"
              className="mb-2 text-[11px] text-muted-foreground italic border border-dashed border-border rounded px-2 py-1"
            >
              {String(d.dormant_label || dormantNodeLabel(d.dormant_since_run as string | null))}
              {Array.isArray(d.collapsed_members) ? '' : '. Its edges are findings of earlier runs; it is left out of every total.'}
            </div>
          )}
          {d.dormant !== true && (() => {
            const touching = connectedEdgeData(selected.id);
            return touching.length > 0 && touching.every((x) => x.coverage_gap === true);
          })() && (
            <div
              data-testid="fusion-coverage-gap-node-note"
              className="mb-2 text-[11px] text-muted-foreground italic border border-dashed border-border rounded px-2 py-1"
            >
              Every edge of this wallet is a coverage gap: the run did not read it. It stays active and in the totals.
            </div>
          )}
          <Row k="id" v={String(d.id || selected.id)} />
          {d.type !== 'group_box' && risk !== undefined && <Row k="risk" v={`${Math.round(risk * 100)}%`} />}
          {!!d.phase && d.type !== 'group_box' && !Array.isArray(d.collapsed_members) && <Row k="phase" v={String(d.phase)} />}
          {!!d.group && d.type !== 'group_box' && !Array.isArray(d.collapsed_members) && <Row k="group" v={String(d.group)} />}
          {!!d.chain && <Row k="chain" v={String(d.chain)} />}
          {typeof d.txn_count === 'number' && <Row k="transactions" v={String(d.txn_count)} />}
          {['wallet', 'address', 'exchange'].includes(String(d.type)) && <LedgerTransfersLine data={d} caseId={caseId} />}
          {!!d.placement_role && <Row k="role" v={String(d.placement_role)} />}
          <ClusterExpand data={d} onExpand={onExpandCluster} />
          <DeclaredTotals data={d} />
          {hasDeepDive(d.deep_dive) && <CryptoDeepDivePanel data={d} />}
          <NodeRiskLevel data={d} />
          {(d.seizure_target === true || !!d.seizure || !!d.target || !!d.finding) && (
            <FindingReviewBlock data={d} caseId={caseId} />
          )}
          <Extras extras={d.module_extras as Record<string, unknown> | undefined} />
          <Activity txns={Array.isArray(d._txns) ? (d._txns as TxDetail[]) : (Array.isArray(d.txns) ? (d.txns as TxDetail[]) : undefined)} />
          {d.type !== 'group_box' && d.type !== 'cluster' && <WorkbenchAction data={d} caseId={caseId} />}
          {d.type !== 'group_box' && d.type !== 'cluster' && <LinkToSubjectAction data={d} caseId={caseId} />}
          {d.type !== 'group_box' && d.type !== 'cluster' && <NodeNotesBlock data={d} caseId={caseId} />}
          <div className="mt-3 text-[10px] text-muted-foreground italic border-t border-border pt-2">
            Projected from the case graph. Full ledger detail lives in StatementFlow / Workbench.
          </div>
        </div>
      );
    }
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: MODULE_COLOR[n.module as FusionModule] }} />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{MODULE_LABEL[n.module as FusionModule]} · {n.type}</span>
        </div>
        <div className="font-semibold text-sm mb-1.5 break-all">{n.label}</div>
        {n.semantic_role && (
          <div className="mb-2"><SemanticRoleChip role={n.semantic_role} /></div>
        )}
        <Row k="id" v={n.id} />
        <Row k="risk" v={`${Math.round((n.risk_score ?? 0) * 100)}%`} />
        {n.placement_role && <Row k="role" v={n.placement_role} />}
        {hasDeepDive(n.deep_dive) && (
          <CryptoDeepDivePanel data={n as unknown as FusionNodeData} />
        )}
        <NodeRiskLevel data={n as unknown as Record<string, unknown>} />
        {n.attribution ? (
          <div className="mt-2 border-t border-border pt-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Attribution</div>
            <div className="mb-1"><TierBadge tier={n.attribution.tier} /></div>
            <Row k="method" v={n.attribution.method} />
            <Row k="confidence" v={n.attribution.confidence.toFixed(2)} />
            <Row k="source" v={n.attribution.source || '—'} />
            {n.attribution.evidence_ref && <Row k="evidence" v={n.attribution.evidence_ref} />}
          </div>
        ) : (
          <div className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">
            Grouping container (presentation only); carries no evidence attribution.
          </div>
        )}
        <Activity txns={n.txns} />
        <Extras extras={n.module_extras} />
        <WorkbenchAction data={n as unknown as FusionNodeData} caseId={caseId} />
        <LinkToSubjectAction data={n as unknown as Record<string, unknown>} caseId={caseId} />
        {caseId && <NodeNotesBlock data={n as unknown as Record<string, unknown>} caseId={caseId} />}
      </div>
    );
  }

  const e = edgeById(selected.id);
  if (!e) {
    // Real-case edge — projected from the case graph. Render what the projection carries.
    const d = liveData(selected.id);
    if (!d) return null;
    if (d.edge_type === 'subject_link') {
      return <SubjectLinkInspectorPanel data={d} edgeId={selected.id} caseId={caseId} />;
    }
    const det: 'deterministic' | 'scored' = d.determinism === 'scored' ? 'scored' : 'deterministic';
    // Resolve endpoints to their human labels (fall back to the id) so the panel
    // reads "Subject → ICICI account", not "ent:subject:case → sf:acct:a8".
    const srcLabel = String(liveData(String(d.source))?.label || d.source || '');
    const tgtLabel = String(liveData(String(d.target))?.label || d.target || '');
    const edgeLabel = String(d._edgeLabel || '').trim();
    const unit = typeof d.token_symbol === 'string' && d.token_symbol
      ? d.token_symbol
      : (typeof d.unit === 'string' && d.unit ? d.unit : undefined);
    const collapsedCount = typeof d.collapsed_edge_count === 'number' ? d.collapsed_edge_count : 0;
    const mergedAmounts = formatAmountsByToken(amountsObj(d.amounts_by_token));
    const transfers = Array.isArray(d.transfers) ? (d.transfers as EdgeTransfer[]) : [];
    const edgeAttr = (d.attribution && typeof d.attribution === 'object' ? d.attribution : {}) as Record<string, unknown>;
    const mergedHashes = transfers.length ? [] : (Array.isArray(d.merged_tx_hashes) ? (d.merged_tx_hashes as unknown[]).map(String) : []);
    const fullTime = formatUtcDateTime(d.timestamp);
    const dwell = formatDwell(d.dwell_seconds);
    return (
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{String(d.edge_type || 'edge')} edge</div>
        <div className="text-sm font-medium mb-2 break-all">{srcLabel} → {tgtLabel}</div>
        {edgeLabel && <div className="text-xs text-foreground mb-2">{edgeLabel}</div>}
        <div className="mb-2"><DetBadge d={det} /></div>
        {d.coverage_gap === true && (
          <div
            data-testid="fusion-coverage-gap-note"
            className="mb-2 text-[11px] text-muted-foreground italic border border-dashed border-border rounded px-2 py-1"
          >
            {coverageGapNote(d.coverage_gap_run as string | null)}
          </div>
        )}
        <Row k="type" v={String(d.edge_type || '—')} />
        {edgeLabel && <Row k="label" v={edgeLabel} />}
        {typeof d.value === 'number' && !!d.value && collapsedCount <= 1 && <Row k="value" v={edgeValueText(d.value, unit)} />}
        {collapsedCount > 1 && <Row k="merged transfers" v={`${collapsedCount} (macro view; totals not summed across tokens)`} />}
        {collapsedCount > 1 && transfers.length <= 1 && <Row k="totals per token" v={mergedAmounts || 'not available'} />}
        {transfers.length <= 1 && typeof d.tx_hash === 'string' && d.tx_hash && <Row k="tx hash" v={<span className="font-mono text-[11px]">{d.tx_hash}</span>} />}
        {transfers.length <= 1 && d.timestamp !== undefined && d.timestamp !== null && d.timestamp !== '' && <Row k="time" v={fullTime || String(d.timestamp)} />}
        {transfers.length > 1 && <Row k="totals per token" v={mergedAmounts || formatAmountsByToken(sumTransfers(transfers)) || 'not available'} />}
        {(!!edgeAttr.first_timestamp || !!edgeAttr.last_timestamp) && (
          <Row k="first / last" v={`${formatUtcDateTime(edgeAttr.first_timestamp) || 'not available'} → ${formatUtcDateTime(edgeAttr.last_timestamp) || 'not available'}`} />
        )}
        {dwell && <Row k="dwell" v={dwell.replace(/^dwell /, '')} />}
        {!!d.phase && <Row k="phase" v={String(d.phase)} />}
        {!!d.group && <Row k="group" v={String(d.group)} />}
        {transfers.length > 0 && (
          <div className="mt-2 border-t border-border pt-2" data-testid="fusion-edge-transfers">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Transfers · {transfers.length}</div>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {transfers.map((t, i) => (
                <div key={`${t.tx_hash || 'tx'}:${i}`} className="rounded-md border border-border bg-secondary/40 px-2 py-1">
                  <div className="flex justify-between gap-2 text-[11px]">
                    <span className="font-semibold">
                      {typeof t.amount === 'number' ? `${t.amount.toLocaleString('en-US', { maximumFractionDigits: 8 })}${t.token ? ` ${t.token}` : ''}` : 'amount not available'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{formatUtcDateTime(t.timestamp) || 'time not available'}</span>
                  </div>
                  <div className="font-mono text-[10px] break-all text-muted-foreground">{t.tx_hash || 'tx hash not available'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {mergedHashes.length > 0 && (
          <div className="mt-2 border-t border-border pt-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Merged transactions · {mergedHashes.length}</div>
            <div className="space-y-0.5 max-h-40 overflow-y-auto">
              {mergedHashes.map((h) => <div key={h} className="font-mono text-[10px] break-all">{h}</div>)}
            </div>
          </div>
        )}
        {typeof d.kind === 'string' && d.kind && <Row k="kind" v={d.kind.replace(/_/g, ' ')} />}
        {d.is_rapid_sweep === true && <Row k="pattern" v="rapid sweep (detector)" />}
        <div className="mt-3 text-[10px] text-muted-foreground italic border-t border-border pt-2">
          {det === 'scored'
            ? 'Inferred link (scored), not a ledger fact. Drawn dashed and dimmed under Court-safe view.'
            : 'Deterministic ledger relation projected from the case graph.'}
        </div>
      </div>
    );
  }
  const s = nodeById(e.source), t = nodeById(e.target);
  return (
    <div className="p-4">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{EDGE_LABEL[e.edge_type] || e.edge_type} edge</div>
      <div className="text-sm font-medium mb-2 break-all">{s?.label || e.source} → {t?.label || e.target}</div>
      <div className="mb-2"><DetBadge d={e.determinism} /></div>
      <Row k="type" v={e.edge_type} />
      {!!e.value && <Row k="value" v={`${e.value.toLocaleString()} ${e.unit || ''}`} />}
      {!!e.count && <Row k="events" v={e.count} />}
      <Row k="join key" v={e.join_key || '—'} />
      <div className="mt-2 border-t border-border pt-2">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Attribution</div>
        <div className="mb-1"><TierBadge tier={e.attribution.tier} /></div>
        <Row k="method" v={e.attribution.method} />
        <Row k="confidence" v={e.attribution.confidence.toFixed(2)} />
        <Row k="source" v={e.attribution.source} />
      </div>
    </div>
  );
}
